'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { BookOpenIcon } from '@phosphor-icons/react/dist/csr/BookOpen';

import type { ExplainerNavCluster } from '../types';
import styles from './Sidebar.module.css';

interface Props {
  nav: ExplainerNavCluster[];
  activeClusterKey: string;
  activeSlug: string;
  onOpenSearch: () => void;
  onNavigate?: () => void;
}

/* Nav labels come from scraped HTML and can carry entities (e.g. "age &amp;
   sex"). Decode the common ones for display. */
const decode = (s: string) =>
  s
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&nbsp;/g, ' ');

/* Docs sidebar.
 *
 *   [🔍 Search…                       ⌘K]
 *
 *   ▣ EXPLAINERS
 *    ⌄ Heart Rate Variability
 *    │  What is HRV?            ← active
 *    │  What's a good HRV?
 *    › Resting Heart Rate
 *    › Glycemic Index
 *
 * Pure tree — rendered in the desktop column and the mobile sheet. */
const Sidebar = ({
  nav,
  activeClusterKey,
  activeSlug,
  onOpenSearch,
  onNavigate,
}: Props) => {
  const t = useTranslations('science');
  const [open, setOpen] = useState<Set<string>>(
    () => new Set([activeClusterKey]),
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  /* Keep the active cluster expanded on every navigation. The useState
     initializer only runs on first mount, but the shell + sidebar persist
     across client-side route changes (e.g. arriving via the search bar), so
     without this the newly-opened article's category would stay collapsed. */
  useEffect(() => {
    setOpen((prev) =>
      prev.has(activeClusterKey) ? prev : new Set(prev).add(activeClusterKey),
    );
  }, [activeClusterKey]);

  /* Bring the selected item into view within the sidebar's OWN scroll area
     (never the window), so a deep article reached via search is visible as
     selected rather than scrolled off. */
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || !activeSlug) return;
    const el = scroller.querySelector<HTMLElement>('[aria-current="page"]');
    if (!el) return;
    const er = el.getBoundingClientRect();
    const sr = scroller.getBoundingClientRect();
    if (er.top < sr.top || er.bottom > sr.bottom) {
      scroller.scrollTop +=
        er.top - sr.top - (scroller.clientHeight - el.offsetHeight) / 2;
    }
  }, [activeSlug, open]);

  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <nav
      className={styles.nav}
      aria-label={t('science.explainers.nav.explainersNav')}
    >
      <button
        type="button"
        className={styles.search}
        onClick={onOpenSearch}
        aria-label={t('science.explainers.hero.searchLabel')}
      >
        <svg
          className={styles.glass}
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <span className={styles.searchText}>
          {t('science.explainers.nav.searchPlaceholder')}
        </span>
        <span className={styles.kbd} aria-hidden="true">
          ⌘K
        </span>
      </button>

      <div className={styles.scroll} ref={scrollRef}>
        <div className={styles.sectionHead}>
          <span className={styles.sectionIcon} aria-hidden="true">
            <BookOpenIcon weight="regular" size={17} />
          </span>
          {t('science.explainers.hero.eyebrow')}
        </div>

        <div className={styles.tree}>
          {nav.map((cluster) => {
            const isOpen = open.has(cluster.key);
            const isActive = cluster.key === activeClusterKey;
            /* The cluster heading only darkens when its OWN category page is
               open (no article selected) — not when an article inside it is
               being read. */
            const isCategoryActive = isActive && !activeSlug;
            return (
              <div className={styles.block} key={cluster.key}>
                <div className={styles.row}>
                  <button
                    type="button"
                    className={styles.chevronBtn}
                    aria-expanded={isOpen}
                    aria-label={
                      isOpen
                        ? t('science.explainers.nav.collapse', {
                            name: decode(cluster.name),
                          })
                        : t('science.explainers.nav.expand', {
                            name: decode(cluster.name),
                          })
                    }
                    onClick={() => toggle(cluster.key)}
                  >
                    <svg
                      className={styles.chevron}
                      data-open={isOpen ? 'true' : undefined}
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="m9 6 6 6-6 6" />
                    </svg>
                  </button>
                  <Link
                    className={styles.rowLink}
                    href={`/science/explainers/category/${cluster.key}/`}
                    data-active={isCategoryActive ? 'true' : undefined}
                    onClick={onNavigate}
                  >
                    {decode(cluster.name)}
                  </Link>
                </div>

                {isOpen && (
                  <ul className={styles.leaves}>
                    {cluster.articles.map((a) => {
                      const current = a.slug === activeSlug;
                      return (
                        <li key={a.slug}>
                          <Link
                            className={styles.leaf}
                            href={`/science/explainers/${a.slug}/`}
                            data-active={current ? 'true' : undefined}
                            aria-current={current ? 'page' : undefined}
                            onClick={onNavigate}
                          >
                            {decode(a.navLabel)}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default Sidebar;
